<template>
  <!-- Trigger slot: renders in DOM to serve as positioning anchor -->
  <span ref="triggerEl" class="floating-popup-trigger">
    <slot name="trigger" :isOpen="isOpen" :toggle="toggle" />
  </span>

  <!-- Content teleported to body for proper positioning and z-stacking -->
  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="popupEl"
      class="floating-popup"
      :class="popupClass"
      :style="popupStyle"
      role="region"
      :aria-label="ariaLabel"
    >
      <slot name="content" :close="close" :isOpen="isOpen" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useAnchoredDropdown } from "@/composables/useAnchoredDropdown";
import type { AnchoredDropdownOptions } from "@/composables/useAnchoredDropdown";

interface FloatingPopupProps extends AnchoredDropdownOptions {
  /** Accessible name for the popup region */
  ariaLabel?: string;
  /** Additional CSS classes for the popup */
  popupClass?: string | Record<string, boolean>;
}

const props = withDefaults(defineProps<FloatingPopupProps>(), {
  ariaLabel: "Floating popup",
  popupClass: "",
  minWidth: 220,
  maxWidth: 400,
  gap: 4,
  closeOnOutsideClick: true,
});

const triggerEl = ref<HTMLElement | null>(null);
const popupEl = ref<HTMLElement | null>(null);

const { isOpen, dropdownStyle, open, close, toggle } = useAnchoredDropdown(
  triggerEl,
  popupEl,
  {
    minWidth: props.minWidth,
    maxWidth: props.maxWidth,
    gap: props.gap,
    closeOnOutsideClick: props.closeOnOutsideClick,
  }
);

const popupStyle = computed(() => ({
  ...dropdownStyle.value,
  zIndex: "1500",
}));

defineExpose({ isOpen, open, close, toggle });
</script>

<style scoped lang="scss">
.floating-popup-trigger {
  display: contents;
}

.floating-popup {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  overflow: auto;
  animation: fadeIn 120ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
